/**
 * Canvas2D fighter rendering — batched Path2D draws.
 *
 * Instead of ~N individual save/beginPath/fill/stroke/restore calls, fighters
 * are sorted into buckets keyed by (colour × strokeWidth × alpha) and one
 * fill() + stroke() is issued per bucket.  For a 4000-fighter battle that
 * collapses thousands of canvas state changes into ~20-30 submissions.
 */
import { ICON_UNIT_TYPES, drawFrozenBorder } from './primitives.js';
import { getUnitColor } from '../../render_utils.js';

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 */
export function renderFightersBatched(r) {
    const ctx   = r.context;
    const units = r.entitySystem.units;

    // Shared stealth glint value — same for every stealth fighter this frame.
    const stealthCycleTime = 4.0;
    const stealthPhase = (r.entitySystem.time % stealthCycleTime) / stealthCycleTime;
    const glintVal = 1.0 - Math.abs(stealthPhase - 0.5) / 0.07;
    const hasGlint = glintVal > 0;

    // bucket key → { dotPath, linePath, color, sw, alpha }
    const bodyBuckets  = new Map();
    // faction → Path2D of attack lines
    const attackPaths  = new Map();
    // single path for all stealth glint stars this frame
    let glintPath      = null;
    // frozen fighters drawn per-entity after the batched flush
    const frozenUnits  = [];

    for (let i = 0; i < units.length; i++) {
        const entity = units[i];
        if (ICON_UNIT_TYPES.has(entity.type)) continue; // non-fighter, handled separately

        const alpha = entity.visual?.alpha ?? 1.0;
        const color = getUnitColor(entity, r.unitFighterColors);
        const baseW = entity.subtype === 'mega'   ? 1.2 :
                      entity.subtype === 'heavy'  ? 1.1 :
                      entity.subtype === 'medium' ? 1.0 : 0.9;
        // Scale by actual entity.size so Enlarging Ray visually enlarges fighters.
        // Default fighter size is 2, so dividing by 2 leaves defaults unchanged.
        const sw    = baseW * (entity.size / 2);

        const key = `${color}::${sw}::${alpha}`;
        let bucket = bodyBuckets.get(key);
        if (!bucket) {
            bucket = { dotPath: new Path2D(), linePath: new Path2D(), color, sw, alpha };
            bodyBuckets.set(key, bucket);
        }

        const vx10 = entity.velX / 10;
        const vy10 = entity.velY / 10;
        if (vx10 > 1 || vy10 > 1 || vx10 < -1 || vy10 < -1) {
            bucket.linePath.moveTo(entity.x, entity.y);
            bucket.linePath.lineTo(entity.x - entity.velX / 10, entity.y - entity.velY / 10);
        } else {
            // rect is faster than moveTo+arc for tiny dots and avoids connecting-line artefacts.
            bucket.dotPath.rect(entity.x - sw, entity.y - sw, sw * 2, sw * 2);
        }

        // Attack line: randomised tip for visual flicker — inline to avoid extra function call.
        if (entity.combat?.shootsTill > entity.time && entity.combat.target) {
            let ap = attackPaths.get(entity.faction);
            if (!ap) { ap = new Path2D(); attackPaths.set(entity.faction, ap); }
            ap.moveTo(entity.x, entity.y);
            ap.lineTo(
                entity.combat.target.x + (Math.random() * 2 - 1),
                entity.combat.target.y + (Math.random() * 2 - 1)
            );
        }

        if (entity.stealthCycleTime > 0 && hasGlint) {
            if (!glintPath) glintPath = new Path2D();
            const rad = 4;
            for (let j = 0; j < 4; j++) {
                const a = (j * Math.PI) / 4;
                glintPath.moveTo(entity.x, entity.y);
                glintPath.lineTo(entity.x + Math.cos(a) * rad, entity.y + Math.sin(a) * rad);
            }
        }

        if (entity.isFrozen && entity.isFrozen()) frozenUnits.push(entity);
    }

    // --- Flush: body buckets ---
    for (const [, b] of bodyBuckets) {
        ctx.globalAlpha = b.alpha;
        ctx.fillStyle   = b.color;
        ctx.fill(b.dotPath);
        ctx.strokeStyle = b.color;
        ctx.lineWidth   = b.sw;
        ctx.stroke(b.linePath);
    }
    ctx.globalAlpha = 1.0;

    // --- Flush: attack lines ---
    ctx.lineWidth = 0.5;
    for (const [faction, ap] of attackPaths) {
        ctx.strokeStyle = r.unitAttackColors[faction];
        ctx.stroke(ap);
    }

    // --- Flush: stealth glints ---
    if (glintPath) {
        ctx.save();
        ctx.globalAlpha = glintVal * 0.8;
        ctx.strokeStyle = 'white';
        ctx.lineWidth   = 0.8;
        ctx.stroke(glintPath);
        ctx.restore();
    }

    // --- Frozen borders: per-entity (rare) ---
    for (let i = 0; i < frozenUnits.length; i++) {
        drawFrozenBorder(frozenUnits[i], ctx);
    }

    ctx.lineWidth = 2; // restore default for callers
}
